import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AuditionPhaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunityId: string;
  invitationId: string;
  scriptUrl?: string | null;
}

export const AuditionPhaseModal = ({ isOpen, onClose, opportunityId, invitationId, scriptUrl }: AuditionPhaseModalProps) => {
  const [showOnlineForm, setShowOnlineForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadSelfTape = async () => {
    if (!selectedFile) {
      toast({
        title: "Error",
        description: "Please select a file to upload",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);

      const fileExt = selectedFile.name.split('.').pop();
      const filePath = `${opportunityId}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('self-tapes')
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('self-tapes')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('opportunity_invitations')
        .update({
          self_tape_url: publicUrl,
          self_tape_submitted_at: new Date().toISOString()
        })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Self tape uploaded successfully",
      });

      setSelectedFile(null);
      onClose();
    } catch (error) {
      console.error('Error uploading self tape:', error);
      toast({
        title: "Error",
        description: "Failed to upload self tape. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Audition Phase</DialogTitle>
        </DialogHeader>

        {!showOnlineForm ? (
          <div className="space-y-6">
            <p className="text-center text-lg">How would you like to apply for this opportunity?</p>
            <div className="flex justify-center gap-4">
              <Button onClick={() => setShowOnlineForm(true)} className="w-40">
                Apply Online
              </Button>
              <Button variant="outline" className="w-40">
                Apply in Person
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {scriptUrl && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Script</h3>
                <iframe
                  src={scriptUrl}
                  className="w-full h-[400px] border rounded"
                  title="Script Viewer"
                />
              </div>
            )}

            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-2">Self Tape Submission</h3>
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
                <Button
                  onClick={handleUploadSelfTape}
                  disabled={!selectedFile || uploading}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {uploading ? 'Uploading...' : 'Upload Self Tape'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};